import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { sleep } from '~/lib/sleep'

export const useSuggestedAltText = (imageUrl: string | null) => {
  const enabled = imageUrl !== null

  const queryResult = useQuery(
    ['suggested-alt-text', imageUrl],
    async () => {
      if (!imageUrl) {
        return null
      }

      const response = await fetch('/api/predictions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'image-alt-text', imageUrl }),
      })
      const createPredictionRes = await response.json()
      const { prediction } = z
        .object({ prediction: z.object({ id: z.string() }) })
        .parse(createPredictionRes)

      let output: string | null | undefined = null
      let status = 'pending'
      let pingCount = 0
      let error: string | null | undefined = null
      while (status !== 'succeeded' && status !== 'failed' && pingCount < 10) {
        await sleep(1000)
        pingCount++
        const response = await fetch(`/api/predictions/${prediction.id}`, {
          headers: { 'Content-Type': 'application/json' },
        })
        const pingPredictionRes = await response.json()
        const { prediction: resolvedPrediction } = z
          .object({
            prediction: z.object({
              output: z.string().nullable().optional(),
              error: z.string().nullable().optional(),
              status: z.union([
                z.literal('starting'),
                z.literal('succeeded'),
                z.literal('failed'),
                z.literal('processing'),
              ]),
            }),
          })
          .parse(pingPredictionRes)
        status = resolvedPrediction.status
        output = resolvedPrediction.output
        error = resolvedPrediction.error
      }

      if (!output) {
        throw new Error(
          'prediction failed: ' + (error ? error : 'unknown error')
        )
      }

      // clean up
      const ouputWithoutPrefix = output.replace('Caption: ', '')
      return (
        ouputWithoutPrefix.charAt(0).toUpperCase() + ouputWithoutPrefix.slice(1)
      )
    },
    { enabled, refetchOnWindowFocus: false, staleTime: Infinity }
  )

  return queryResult
}
